import { Component, OnInit, OnDestroy, Input, OnChanges, SimpleChanges } from '@angular/core';
import { PriceHelper } from '../helpers/PriceHelper';
import { DateHelper } from '../helpers/DateHelper';
import * as Chart from 'chart.js';
import { BaseComponent } from '../classes/BaseComponent';
import { ThemeService, ThemeListener } from '../theme.service';

@Component({
  selector: 'app-graph',
  templateUrl: './graph.component.html',
  styleUrls: ['./graph.component.scss']
})
export class GraphComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy, ThemeListener {

  // Color constants
  private static readonly COLOR_RED = '#EA0000';
  private static readonly COLOR_GOLD = '#e1c02d';
  private static readonly COLOR_GREEN = '#5AC45A';
  private static readonly COLOR_GRAY = '#BBB';
  private static readonly COLOR_BLUE = '#00BAF7';

  // Label drawing constants
  private static readonly LABEL_CIRCLE_RADIUS = 5;
  private static readonly LABEL_CIRCLE_SPACING = 19;
  private static readonly LABEL_PADDING = 4;
  private static readonly LABEL_BG_HEIGHT = 14;
  private static readonly LABEL_OFFSET_ABOVE = 8;
  private static readonly LABEL_OFFSET_BELOW = 8;
  private static readonly LABEL_CIRCLE_OFFSET_UP = 2;
  private static readonly LABEL_FONT_SIZE = 12;
  private static readonly LABEL_FONT_FAMILY = 'Arial';

  // Chart layout constants
  private static readonly CHART_PADDING_TOP = 25;
  private static readonly CHART_PADDING_BOTTOM = 40;
  private static readonly CHART_POINT_RADIUS = 5;
  private static readonly CHART_POINT_BORDER_WIDTH = 2;
  private static readonly CHART_POINT_HOVER_BORDER_WIDTH = 3;
  private static readonly CHART_LINE_WIDTH = 2;
  private static readonly CHART_CANVAS_HEIGHT = 250;

  // Cached marked points
  private cachedMarkedPoints: { lowest: any, highest: any, current: any } = null;
  private cachedDataHash: string = null;

  themeUpdated() {
    //we need to re-draw the chart so the colors are updated
    this.createChart();
    // Labels will be drawn automatically by plugin afterDraw hook
  }

  @Input() data: GraphData;
  @Input() yAxisUnit = "EUR";

  unfilteredData: GraphData = new GraphData([], []);

  dropdownItems = [
    {type: 0, text: "allTime"},
    {type: 1, text: "thisYear"},
    {type: 2, text: "lastYear"},
    { type: 3, text: "last2Years" },
    { type: 4, text: "last3Years" },
  ];
  selectedTimeSpan = this.dropdownItems[0];

  chart: Chart = null;
  priceLabelsPlugin: any = null;

  constructor(
    private themeService: ThemeService
  ) {
    super();
    themeService.addListener(this);

    // Create plugin that redraws labels after every chart draw
    const self = this;
    this.priceLabelsPlugin = {
      afterDraw: function (chartInstance) {
        if (chartInstance === self.chart) {
          self.drawPriceLabels();
        }
      }
    };
  }

  getMarkedPoints(): { lowest: any, highest: any, current: any } {
    // Generate hash of current data for caching
    let dataHash = '';
    if (this.data && this.data.series && this.data.series[0]) {
      dataHash = JSON.stringify(this.data.series[0].map(p => ({ x: p.x, y: p.y })));
    }

    // Return cached result if data hasn't changed
    if (this.cachedMarkedPoints && this.cachedDataHash === dataHash) {
      return this.cachedMarkedPoints;
    }

    // Clear cache if data hash changed
    if (this.cachedDataHash !== null && this.cachedDataHash !== dataHash) {
      this.cachedMarkedPoints = null;
    }

    const filteredData = this.data && this.data.series ? this.data.series[0] : null;
    if (!filteredData || filteredData.length === 0) {
      this.cachedMarkedPoints = { lowest: null, highest: null, current: null };
      this.cachedDataHash = dataHash;
      return this.cachedMarkedPoints;
    }

    let lowestPrice = Number.POSITIVE_INFINITY;
    let highestPrice = Number.NEGATIVE_INFINITY;
    let lowestIndex = -1;
    let highestIndex = -1;
    const currentIndex = filteredData.length - 1;

    // Find first occurrence of lowest and highest price
    for (let i = 0; i < filteredData.length; i++) {
      const price = Number.parseFloat(filteredData[i].y);
      if (price < lowestPrice) {
        lowestPrice = price;
        lowestIndex = i;
      }
      if (price > highestPrice) {
        highestPrice = price;
        highestIndex = i;
      }
    }

    this.cachedMarkedPoints = {
      lowest: lowestIndex >= 0 ? { index: lowestIndex, point: filteredData[lowestIndex] } : null,
      highest: highestIndex >= 0 ? { index: highestIndex, point: filteredData[highestIndex] } : null,
      current: currentIndex >= 0 ? { index: currentIndex, point: filteredData[currentIndex] } : null
    };
    this.cachedDataHash = dataHash;
    return this.cachedMarkedPoints;
  }

  createChart() {
    const self = this;
    const ctx = document.getElementById('myChart');
    // Ensure data exists and has series
    if (!this.data || !this.data.series || !this.data.series[0] || this.data.series[0].length === 0) {
      return;
    }
    if (!this.unfilteredData || !this.unfilteredData.series || !this.unfilteredData.series[0] || this.unfilteredData.series[0].length <= 1) {
      return;
    }
    const data = this.data.series[0];
    if (ctx) {

      const showLine = this.data.series[0] && this.data.series[0].length > 0;

      const dates = this.data.series[0].map(x => x.x);
      const indicators = this.data.series[0].map(x => x.indicator);
      const isBest = this.data.series[0].map(x => x.isBest);

      const isDarkTheme: boolean = this.themeService.isDarkTheme();

      this.chart && this.chart.destroy();//destroy previous chart

      // Register plugin if not already registered
      const allPlugins = Chart.plugins.getAll ? Chart.plugins.getAll() : [];
      if (!allPlugins.find(p => p === this.priceLabelsPlugin)) {
        Chart.plugins.register(this.priceLabelsPlugin);
      }

      this.chart = new Chart(ctx, {
        type: 'line',
        data: {
          //labels: labels,
          datasets: [{
            label: self.getString("price"),
            data: data,
            showLine: showLine,
            steppedLine: "before",
            backgroundColor: "rgba(0,0,0,0)",
            borderColor: GraphComponent.COLOR_BLUE,
            borderWidth: GraphComponent.CHART_LINE_WIDTH,
            pointRadius: GraphComponent.CHART_POINT_RADIUS,
            pointBorderWidth: GraphComponent.CHART_POINT_BORDER_WIDTH,
            pointBackgroundColor: isDarkTheme? "#000" : "#FFF",
            pointHoverBackgroundColor: GraphComponent.COLOR_BLUE,
            pointHoverBorderColor: GraphComponent.COLOR_BLUE,
            pointHoverBorderWidth: GraphComponent.CHART_POINT_HOVER_BORDER_WIDTH,
          }]
        },
        options: {
          layout: {
            padding: {
              top: GraphComponent.CHART_PADDING_TOP,
              bottom: GraphComponent.CHART_PADDING_BOTTOM,
              left: 0,
              right: 0
            }
          },
          scales: {
            yAxes: [{
              ticks: {
                beginAtZero: false,
                min: self.getMinPriceFromData(),
                max: self.getMaxPriceFromData(),
                callback: function(label, index, labels) {
                  return PriceHelper.formatPrice(label, self.yAxisUnit);
                }
              },
              afterBuildTicks: function (scale) {
                const minPrice = self.getMinPriceFromData();
                const maxPrice = self.getMaxPriceFromData();
                const middlePrice = self.calculateRoundedMiddlePrice(minPrice, maxPrice);
                scale.ticks = [maxPrice, middlePrice, minPrice];
              },
              gridLines: {
                color: isDarkTheme ? "#484848" : "#DDD"
              }
            }],
            xAxes: [{
              type: 'time',
              bounds: 'ticks',
              time: {
                unit: 'month',
                stepSize: 1
              },
              ticks: {
                source: 'auto',
                autoSkip: false,
                minRotation: 45,
                maxRotation: 45,
                callback: function(label, index, labels) {
                  if (!labels || index == null || index < 0 || index >= labels.length) {
                    return '';
                  }

                  const labelCount = labels.length;
                  if (!self.shouldShowXAxisLabel(index, labelCount, 5)) {
                    return '';
                  }

                  const tick: any = labels[index];
                  const rawValue: any = tick && tick.value ? tick.value : tick;
                  const date: Date = new Date(rawValue);
                  if (!date || isNaN(date.getTime())) {
                    return '';
                  }

                  const year = date.getFullYear().toString().substr(-2);
                  return self.getString("monthsShort").split(',')[date.getMonth()] + ' ' + year;
                }
              },
              gridLines: {
                display: false
              },
            }],
          },
          legend: {
            display: false,
            position: "bottom"
          },
          tooltips: {
            callbacks: {
              title: function (tooltipItem, data) {
                const date = new Date(dates[tooltipItem[0].index]);
                return DateHelper.getLocaleDateString(date);
              },
              label: function (tooltipItem, data) {
                const price = data.datasets[0].data[tooltipItem.index].y || '';
                return PriceHelper.formatPrice(price, self.yAxisUnit);
              },
              labelColor: function(tooltipItem, chart) {
                return {
                  borderColor: isBest[tooltipItem.index] === true ? GraphComponent.COLOR_GOLD : indicators[tooltipItem.index] === '+' ?
                    GraphComponent.COLOR_RED : indicators[tooltipItem.index] === '-' ? GraphComponent.COLOR_GREEN : GraphComponent.COLOR_GRAY,
                  backgroundColor: isBest[tooltipItem.index] === true ? GraphComponent.COLOR_GOLD : indicators[tooltipItem.index] === '+' ?
                    GraphComponent.COLOR_RED : indicators[tooltipItem.index] === '-' ? GraphComponent.COLOR_GREEN : GraphComponent.COLOR_GRAY
                };
              }
            }
          },
          animation: {
            onComplete: function () {
              // Labels will be drawn by plugin afterDraw hook
            }
          }
        }
      });
    }
  }

  private getCircleColor(pointInfo: any): string {
    if (pointInfo.type === 'highest') {
      return GraphComponent.COLOR_RED;
    } else if (pointInfo.type === 'lowest') {
      // Only gold if this is really the best price (lowest price ever)
      // Otherwise use green to indicate it's a low price in this time range
      return pointInfo.point.isBest ? GraphComponent.COLOR_GOLD : GraphComponent.COLOR_GREEN;
    } else {
      // Current price: determine color based on indicator and isBest
      if (pointInfo.point.isBest) {
        return GraphComponent.COLOR_GOLD;
      } else if (pointInfo.point.indicator === '+') {
        return GraphComponent.COLOR_RED;
      } else if (pointInfo.point.indicator === '-') {
        return GraphComponent.COLOR_GREEN;
      } else {
        return GraphComponent.COLOR_GRAY;
      }
    }
  }

  private calculateLabelPosition(pointInfo: any, y: number): { labelY: number, textBaseline: CanvasTextBaseline } {
    if (pointInfo.type === 'highest') {
      return {
        labelY: y - GraphComponent.LABEL_OFFSET_ABOVE,
        textBaseline: 'bottom' as CanvasTextBaseline
      };
    } else {
      return {
        labelY: y + GraphComponent.LABEL_OFFSET_BELOW,
        textBaseline: 'top' as CanvasTextBaseline
      };
    }
  }

  private drawSingleLabel(ctx: CanvasRenderingContext2D, pointInfo: any, x: number, y: number,
    formattedPrice: string, isDarkTheme: boolean, chartArea?: any): void {
    const position = this.calculateLabelPosition(pointInfo, y);
    const labelY = position.labelY;
    const textBaseline = position.textBaseline;
    const circleColor = this.getCircleColor(pointInfo);

    ctx.textBaseline = textBaseline;

    // Calculate dimensions for circle + text
    const textWidth = ctx.measureText(formattedPrice).width;
    const totalContentWidth = GraphComponent.LABEL_CIRCLE_RADIUS * 2 + GraphComponent.LABEL_CIRCLE_SPACING + textWidth;

    // Get canvas dimensions for boundary checking
    // Note: x and y coordinates from Chart.js are relative to the canvas, not chartArea
    const canvas = ctx.canvas;
    const canvasWidth = canvas.width;
    const minPadding = GraphComponent.LABEL_PADDING;

    // Calculate the actual visible area (accounting for any padding)
    // Use chartArea if available to get more accurate boundaries
    let maxX: number;
    let minX: number;

    if (chartArea && chartArea.right && chartArea.left) {
      // Use chart area boundaries for more accurate positioning
      maxX = chartArea.right;
      minX = chartArea.left;
    } else {
      // Fallback to canvas dimensions
      maxX = canvasWidth;
      minX = 0;
    }

    let bgX = x - (totalContentWidth / 2) - GraphComponent.LABEL_PADDING;
    const bgWidth = totalContentWidth + GraphComponent.LABEL_PADDING * 2;

    // Adjust position if label would overflow right edge - shift only part of the overflow amount
    const rightOverflow = (bgX + bgWidth) - (maxX - minPadding);
    if (rightOverflow > 0) {
      // Only shift by 40% of the overflow to keep label closer to the point
      bgX = bgX - (rightOverflow * 0.4);
    }

    // Adjust position if label would overflow left edge - shift only part of the overflow amount
    const leftOverflow = (minX + minPadding) - bgX;
    if (leftOverflow > 0) {
      // Only shift by 40% of the overflow to keep label closer to the point
      bgX = bgX + (leftOverflow * 0.4);
    }

    // Recalculate text and circle positions based on adjusted bgX
    const textX = bgX + GraphComponent.LABEL_PADDING + (totalContentWidth / 2);
    const circleX = textX - GraphComponent.LABEL_CIRCLE_SPACING - GraphComponent.LABEL_CIRCLE_RADIUS;

    let bgY: number;
    if (pointInfo.type === 'highest') {
      bgY = labelY - GraphComponent.LABEL_BG_HEIGHT;
    } else {
      bgY = labelY;
    }

    // Calculate vertical center of text based on baseline
    const textCenterY = textBaseline === 'bottom' ? labelY - (GraphComponent.LABEL_BG_HEIGHT / 2) : labelY + (GraphComponent.LABEL_BG_HEIGHT / 2);
    // Move circle 2px up from text center only for labels below point (lowest and current)
    // For highest price (above point), keep circle at text center
    const circleY = pointInfo.type === 'highest' ? textCenterY : textCenterY - GraphComponent.LABEL_CIRCLE_OFFSET_UP;

    // Draw background rectangle first
    ctx.fillStyle = isDarkTheme ? "rgba(0, 0, 0, 0.8)" : "rgba(255, 255, 255, 0.9)";
    ctx.fillRect(bgX, bgY, bgWidth, GraphComponent.LABEL_BG_HEIGHT);

    // Draw colored circle before text
    ctx.beginPath();
    ctx.arc(circleX, circleY, GraphComponent.LABEL_CIRCLE_RADIUS, 0, 2 * Math.PI);
    ctx.fillStyle = circleColor;
    ctx.fill();

    // Draw text
    const textColor = isDarkTheme ? "#FFF" : "#000";
    ctx.fillStyle = textColor;
    ctx.fillText(formattedPrice, textX, labelY);
  }

  drawPriceLabels() {
    if (!this.chart || !this.data || !this.data.series[0] || this.data.series[0].length === 0) {
      return;
    }

    const markedPoints = this.getMarkedPoints();
    if (!markedPoints.lowest && !markedPoints.highest && !markedPoints.current) {
      return;
    }

    const chart = this.chart;
    const canvas = chart.canvas;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    const meta = chart.getDatasetMeta(0);
    const isDarkTheme: boolean = this.themeService.isDarkTheme();

    ctx.save();
    ctx.font = GraphComponent.LABEL_FONT_SIZE + "px " + GraphComponent.LABEL_FONT_FAMILY;
    ctx.textAlign = "center";

    const pointsToDraw = [];
    if (markedPoints.lowest) {
      pointsToDraw.push({ index: markedPoints.lowest.index, point: markedPoints.lowest.point, type: 'lowest' });
    }
    if (markedPoints.highest) {
      pointsToDraw.push({ index: markedPoints.highest.index, point: markedPoints.highest.point, type: 'highest' });
    }
    if (markedPoints.current) {
      pointsToDraw.push({ index: markedPoints.current.index, point: markedPoints.current.point, type: 'current' });
    }

    // Remove duplicates if same point appears multiple times
    const uniquePoints = [];
    const seenIndices = new Set();
    for (const pointInfo of pointsToDraw) {
      if (!seenIndices.has(pointInfo.index)) {
        seenIndices.add(pointInfo.index);
        uniquePoints.push(pointInfo);
      }
    }

    for (const pointInfo of uniquePoints) {
      const point = meta.data[pointInfo.index];
      if (point && point._model) {
        const x = point._model.x;
        const y = point._model.y;
        const price = Number.parseFloat(pointInfo.point.y);
        const formattedPrice = PriceHelper.formatPrice(price, this.yAxisUnit);
        this.drawSingleLabel(ctx, pointInfo, x, y, formattedPrice, isDarkTheme, chart.chartArea);
      }
    }

    ctx.restore();
  }

  ngOnInit() {
    if(this.data) {
      this.unfilteredData.labels = this.data.labels.slice();
      this.unfilteredData.series = this.data.series.slice();

      this.loadGraph();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // React to data changes (e.g., when switching between buy/rent)
    if (changes['data']) {
      // Always clear cache when data changes
      this.cachedMarkedPoints = null;
      this.cachedDataHash = null;

      if (this.data && !changes['data'].firstChange) {
        // Update unfiltered data and reload graph
        this.unfilteredData.labels = this.data.labels ? this.data.labels.slice() : [];
        this.unfilteredData.series = this.data.series ? this.data.series.slice() : [];
        this.loadGraph();
      } else if (this.data && changes['data'].firstChange) {
        // First change is handled in ngOnInit
        this.unfilteredData.labels = this.data.labels ? this.data.labels.slice() : [];
        this.unfilteredData.series = this.data.series ? this.data.series.slice() : [];
      } else if (!this.data) {
        // Data was set to null, destroy chart
        if (this.chart) {
          this.chart.destroy();
          this.chart = null;
        }
      }
    }
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
    this.themeService.removeListener(this);
  }

  loadGraph() {
    // Clear cache when reloading graph
    this.cachedMarkedPoints = null;
    this.cachedDataHash = null;
    this.createChart();
    // Labels will be drawn automatically by plugin afterDraw hook
  }


  timeSpanChanged(value) {
    if (!value) { return; }
    // Clear cache when time span changes
    this.cachedMarkedPoints = null;
    this.cachedDataHash = null;

    if (value.text === "allTime") {
      this.data.labels = this.unfilteredData.labels.slice().filter(x => {
        return x.value != null;
      });
      this.data.series[0] = this.unfilteredData.series[0].slice().filter(x => {
        return x.x != null;
      });
    }
    else if (value.text === "thisYear") {
      const currentYear = new Date().getFullYear();
      this.data.labels = this.unfilteredData.labels.slice().filter(x => {
        return x.value != null && (x.value as Date).getFullYear() === currentYear;
      });
      this.data.series[0] = this.unfilteredData.series[0].slice().filter(x => {
        return x.x != null && (x.x as Date).getFullYear() === currentYear;
      });
    }
    else if (value.text === "lastYear") {
      const lastYear = new Date().getFullYear() - 1;
      this.data.labels = this.unfilteredData.labels.slice().filter(x => {
        return x.value != null && (x.value as Date).getFullYear() === lastYear;
      });
      this.data.series[0] = this.unfilteredData.series[0].slice().filter(x => {
        return x.x != null && (x.x as Date).getFullYear() === lastYear;
      });
    }
    else if (value.text === "last2Years") {
      const currentYear = new Date().getFullYear();
      const twoYearsAgo = currentYear - 2;
      this.data.labels = this.unfilteredData.labels.slice().filter(x => {
        if (x.value == null) { return false; }
        const year = (x.value as Date).getFullYear();
        return year >= twoYearsAgo && year <= currentYear;
      });
      this.data.series[0] = this.unfilteredData.series[0].slice().filter(x => {
        if (x.x == null) { return false; }
        const year = (x.x as Date).getFullYear();
        return year >= twoYearsAgo && year <= currentYear;
      });
    }
    else if (value.text === "last3Years") {
      const currentYear = new Date().getFullYear();
      const threeYearsAgo = currentYear - 3;
      this.data.labels = this.unfilteredData.labels.slice().filter(x => {
        if (x.value == null) { return false; }
        const year = (x.value as Date).getFullYear();
        return year >= threeYearsAgo && year <= currentYear;
      });
      this.data.series[0] = this.unfilteredData.series[0].slice().filter(x => {
        if (x.x == null) { return false; }
        const year = (x.x as Date).getFullYear();
        return year >= threeYearsAgo && year <= currentYear;
      });
    }
    this.loadGraph();
  }

  getCanvasHeight(): number {
    if(this.unfilteredData.series[0].length > 1) {
      return GraphComponent.CHART_CANVAS_HEIGHT;
    }
    else {
      return 0;
    }
  }

  /**
   * Returns the minimum price from the current filtered data series.
   */
  getMinPriceFromData(): number {
    if (!this.data || !this.data.series || !this.data.series[0] || this.data.series[0].length === 0) {
      return 0;
    }
    let minPrice = Number.POSITIVE_INFINITY;
    for (const point of this.data.series[0]) {
      const price = Number.parseFloat(point.y);
      if (price < minPrice) {
        minPrice = price;
      }
    }
    return minPrice === Number.POSITIVE_INFINITY ? 0 : minPrice;
  }

  /**
   * Returns the maximum price from the current filtered data series.
   */
  getMaxPriceFromData(): number {
    if (!this.data || !this.data.series || !this.data.series[0] || this.data.series[0].length === 0) {
      return 0;
    }
    let maxPrice = Number.NEGATIVE_INFINITY;
    for (const point of this.data.series[0]) {
      const price = Number.parseFloat(point.y);
      if (price > maxPrice) {
        maxPrice = price;
      }
    }
    return maxPrice === Number.NEGATIVE_INFINITY ? 0 : maxPrice;
  }

  /**
   * Calculates the middle price between min and max, rounded to .99.
   * Example: if middle is 11.15 -> 10.99, if middle is 11.50 -> 11.99
   */
  calculateRoundedMiddlePrice(minPrice: number, maxPrice: number): number {
    const middle = (minPrice + maxPrice) / 2;
    const decimal = middle - Math.floor(middle);
    if (decimal < 0.50) {
      return Math.floor(middle) - 1 + 0.99;
    } else {
      return Math.floor(middle) + 0.99;
    }
  }

  /**
   * Returns true if the current tick index should display a label.
   * We show up to maxLabels labels, always including first and last tick.
   */
  private shouldShowXAxisLabel(index: number, labelCount: number, maxLabels: number): boolean {
    if (labelCount <= 0) { return false; }
    if (labelCount <= maxLabels) { return true; }

    const lastIndex = labelCount - 1;
    if (index === 0 || index === lastIndex) { return true; }

    // Need (maxLabels - 2) intermediate labels
    const intermediate = maxLabels - 2;
    if (intermediate <= 0) { return false; }

    const keepMap: any = {};
    keepMap[0] = true;
    keepMap[lastIndex] = true;

    // Choose evenly spaced indices between 0 and lastIndex
    const step = lastIndex / (intermediate + 1);
    for (let i = 1; i <= intermediate; i++) {
      const idx = Math.round(step * i);
      if (idx > 0 && idx < lastIndex) {
        keepMap[idx] = true;
      }
    }

    return keepMap[index] === true;
  }

}

export class GraphData {
  labels: any[] = [];
  series = [];
  constructor(labels, series) {
    this.labels = labels;
    this.series = series;
  }
}
